import React, { useState, useEffect } from 'react';
import { TextField, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

const PaymentSection = ({ 
  totalIncl, 
  invoicePaidAmount, 
  setInvoicePaidAmount,
  error = false, 
  onFocus,
  disabled = false,
  onFullPayment
}) => {
  const { t } = useTranslation();
  const [showExceedsWarning, setShowExceedsWarning] = useState(false);

  const paidAmount = parseFloat(invoicePaidAmount) || 0;
  const hasError = error || showExceedsWarning;

  // Clear warning after 3 seconds
  useEffect(() => {
    if (showExceedsWarning) {
      const timer = setTimeout(() => setShowExceedsWarning(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showExceedsWarning]);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
      {/* Left: Total Amount */}
      <TextField
        type="number"
        label={t("total_amount")}
        value={totalIncl.toFixed(2)}
        disabled
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': { borderRadius: '10px' },
        }}
        InputProps={{
          startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
          readOnly: true,
        }}
      />

      {/* Middle: Full Payment Button - centered both axes */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pb: (hasError || disabled) ? '22px' : 0 }}>
        <Button
          variant="contained"
          size="medium"
          onClick={onFullPayment}
          disabled={disabled}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: '#ffffff !important',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.5) !important' },
          }}
        >
          {t("full_payment") || "Full Payment"}
        </Button>
      </Box>

      {/* Right: Paid Amount Field */}
      <TextField
        type="number"
        label={t("paid_amount")}
        value={invoicePaidAmount}
        onChange={(e) => {
          const value = e.target.value;
          // Prevent negative values and amounts greater than total
          if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= totalIncl)) {
            setInvoicePaidAmount(value);
            setShowExceedsWarning(false);
          } else if (parseFloat(value) > totalIncl) {
            // User tried to exceed total - show warning
            setShowExceedsWarning(true);
          }
        }}
        onFocus={() => {
          if (onFocus) onFocus();
          setShowExceedsWarning(false);
        }}
        placeholder="0.00"
        disabled={disabled}
        error={hasError}
        helperText={
          showExceedsWarning 
            ? (t("paid_exceeds_total") || "Paid amount is more than the invoice total")
            : error 
              ? t("paid_amount_required") 
              : (disabled ? t("add_products_first") || "Add products to enable payment" : "")
        }
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': { borderRadius: '10px' },
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'error.main',
            borderWidth: '2px',
          },
        }}
        InputProps={{
          startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
          inputProps: { min: 0, max: totalIncl, step: "0.01" }
        }}
      />
    </Box>
  );
};

export default PaymentSection;